import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { rehydrateFromActiveRow } from "../services/rowRehydrator";
import { clearRow, writeBoth, writePractices, writeSites } from "../services/writerService";
const SelectionContext = createContext(undefined);
export const SelectionProvider = ({ hierarchy, children }) => {
    const [selectedPracticeIds, setSelectedPracticeIds] = useState([]);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [lastMessage, setLastMessage] = useState(null);
    const lastAddressRef = useRef(null);
    async function writeSelectedPractices() {
        if (!hierarchy)
            return;
        const names = selectedPracticeIds
            .map((id) => hierarchy.byPracticeId[id]?.name)
            .filter(Boolean);
        const result = await writePractices(names, selectedPracticeIds);
        setLastMessage(result.message + ` (row ${result.row})`);
    }
    async function writeSelectedSites() {
        if (!hierarchy)
            return;
        const names = selectedSiteIds
            .map((id) => hierarchy.bySiteId[id]?.name)
            .filter(Boolean);
        const result = await writeSites(names, selectedSiteIds);
        setLastMessage(result.message + ` (row ${result.row})`);
    }
    async function clearCurrentRow() {
        const result = await clearRow(true);
        setLastMessage(result.message + ` (row ${result.row})`);
    }
    async function writeSelectedBoth() {
        if (!hierarchy)
            return;
        const practiceNames = selectedPracticeIds
            .map((id) => hierarchy.byPracticeId[id]?.name)
            .filter(Boolean);
        const siteNames = selectedSiteIds
            .map((id) => hierarchy.bySiteId[id]?.name)
            .filter(Boolean);
        const result = await writeBoth(practiceNames, selectedPracticeIds, siteNames, selectedSiteIds);
        setLastMessage(result.message + ` (rows ${result.rows.join(", ")})`);
    }
    async function rehydrateSelectionFromRow() {
        try {
            const result = await rehydrateFromActiveRow();
            setSelectedPracticeIds(result.practiceIds);
            setSelectedSiteIds(result.siteIds);
            setLastMessage(result.message + ` (rowsync)`);
        }
        catch (e) {
            console.warn("Rehydrate failed", e);
            setLastMessage("Rehydrate failed");
        }
    }
    // Auto-clear when Excel selection (active cell address) changes.
    useEffect(() => {
        if (!Office?.context?.workbook?.getActiveCell)
            return; // Guard if host not ready
        let disposed = false;
        const hook = async () => {
            try {
                await Excel.run(async (ctx) => {
                    const range = ctx.workbook.getSelectedRange();
                    range.load("address");
                    await ctx.sync();
                    const addr = range.address;
                    if (lastAddressRef.current && lastAddressRef.current !== addr) {
                        // Clear only if we have an existing selection to avoid unnecessary state churn
                        if (selectedPracticeIds.length || selectedSiteIds.length) {
                            setSelectedPracticeIds([]);
                            setSelectedSiteIds([]);
                            setLastMessage("Selection cleared (active cell changed)");
                        }
                    }
                    lastAddressRef.current = addr;
                });
            }
            catch (e) {
                // Non-fatal; do not spam console
            }
            if (!disposed) {
                // Polling approach (lightweight) since event API may not be available in all host contexts.
                setTimeout(hook, 1200); // 1.2s debounce window; adjust if needed
            }
        };
        hook();
        return () => {
            disposed = true;
        };
        // We deliberately exclude deps to keep a single polling loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPracticeIds.length, selectedSiteIds.length]);
    const value = useMemo(() => ({
        selectedPracticeIds,
        selectedSiteIds,
        setPracticeIds: setSelectedPracticeIds,
        setSiteIds: setSelectedSiteIds,
        writeSelectedPractices,
        writeSelectedSites,
        writeSelectedBoth,
        rehydrateSelectionFromRow,
        clearCurrentRow,
        lastMessage,
        hierarchy,
    }), [selectedPracticeIds, selectedSiteIds, hierarchy, lastMessage]);
    return _jsx(SelectionContext.Provider, { value: value, children: children });
};
export function useSelectionContext() {
    const ctx = useContext(SelectionContext);
    if (!ctx)
        throw new Error("useSelection must be used within SelectionProvider");
    return ctx;
}
